import { ComponentType } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './authenticationProvider';

interface PrivateRouteProps {
  component: ComponentType<any>;
  requiredRoles?: string[]; // mehrere Rollen erlaubt (API roles)
}

/**
 * Schützt Routen vor unbefugtem Zugriff.
 * Unterstützt mehrere Rollen und berücksichtigt eine Rollenhierarchie.
 */
export default function PrivateRoute({ component: Component, requiredRoles }: PrivateRouteProps) {
  const { isAuthenticated, account } = useAuth();

  // 🔹 Rollen-Hierarchie definieren (höher = mächtiger)
  const ROLE_HIERARCHY: Record<string, number> = {
    'tenant-owner': 3,
    'tenant-admin': 2,
    'tenant-employee': 1
  };

  // 🔹 Nicht eingeloggt → Login-Seite
  if (!isAuthenticated) {
    return <Navigate to="/cp/login" replace />;
  }

  const userRoles = account?.roles?.map(r => r.toLowerCase()) ?? [];

  // 🔹 Wenn keine Rolle gefordert ist → Zugriff gewähren
  if (!requiredRoles || requiredRoles.length === 0) {
    return <Component />;
  }

  // 🔹 Berechne höchste User-Rolle (nach Hierarchie)
  const userMaxLevel = Math.max(...userRoles.map(r => ROLE_HIERARCHY[r] || 0));

  // 🔹 Berechne niedrigste benötigte Rolle (damit Admin automatisch erlaubt ist)
  const requiredMinLevel = Math.min(...requiredRoles.map(r => ROLE_HIERARCHY[r.toLowerCase()] || 99));

  // 🔹 Zugriff prüfen
  if (userMaxLevel >= requiredMinLevel) {
    return <Component />;
  }

  // 🔹 Keine Berechtigung → 401
  return <Navigate to="/status/401" replace />;
}
